import './internal-clients.css';

import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useMemo, useState } from 'react';

import useArchiveClientAdminUser from '../../api/super-admin/clients/useArchiveClientAdminUser';
import useClientDocumentTemplates from '../../api/super-admin/clients/useClientDocumentTemplates';
import useCreateClientAdminUser from '../../api/super-admin/clients/useCreateClientAdminUser';
import useCreateClientVroomConfiguration from '../../api/super-admin/clients/useCreateClientVroomConfiguration';
import useGetClient from '../../api/super-admin/clients/useGetClient';
import useSaveClient from '../../api/super-admin/clients/useSaveClient';
import useUploadClientLogo from '../../api/super-admin/clients/useUploadClientLogo';
import useGetRequest from '../../api/useGetRequest';
import MainButton from '../../components/agv-button';
import GenericModal from '../../components/agv-generic-modal';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../enums/modal-size.enum';
import AgvPageHeader, {
  PageHeaderButton,
} from '../../redesign-v4/components/page-header';
import {
  CLIENT_SECTION,
  computeJsonDiff,
  getChangeSection,
} from './client-diff';
import {
  isFlagEnabled,
  toClientPayload,
  toClientsOptions,
  toInformation,
} from './client-form';
import { Banner, FormSection, Panel } from './clients-ui';
import ClientChangesReviewModal from './components/client-changes-review-modal';
import ClientHistoryModal from './components/client-history-modal';
import AdminUserStep from './steps/admin-user-step';
import DocumentsStep from './steps/documents-step';
import InformationStep, {
  isInformationStepValid,
} from './steps/information-step';
import OptionsStep from './steps/options-step';
import PricingStep, { isPricingValid } from './steps/pricing-step';

export const SHEET_SECTION = {
  ...CLIENT_SECTION,
  DOCUMENTS: 'documents',
  ADMINS: 'admins',
};

const SECTION_TABS = [
  { key: SHEET_SECTION.INFORMATION, label: 'Informations' },
  { key: SHEET_SECTION.OPTIONS, label: 'Options' },
  { key: SHEET_SECTION.PRICING, label: 'Tarification' },
  {
    key: SHEET_SECTION.DOCUMENTS,
    label: 'Documents (WIP)',
    separatorBefore: true,
  },
  { key: SHEET_SECTION.ADMINS, label: 'Administrateurs' },
];

const VroomSection = ({ vroomConfiguration, onCreate, isCreating }) => (
  <FormSection
    title="Optimisation VROOM"
    description="Configuration utilisée par « Optimiser les tournées ». Personnalisation par client à venir."
  >
    {vroomConfiguration ? (
      <p className="tw-m-0 tw-text-[13px] tw-text-black">
        Configuration « {vroomConfiguration.configuration_name} » · exploration{' '}
        {vroomConfiguration.exploration_level} · {vroomConfiguration.timeout} ms
      </p>
    ) : (
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-3">
        <Banner tone="warn">
          Aucune configuration : l’optimisation du puits est impossible pour ce
          client.
        </Banner>
        <MainButton
          variant="secondary"
          text="Créer la configuration par défaut"
          onClick={onCreate}
          isLoading={isCreating}
        />
      </div>
    )}
  </FormSection>
);

/**
 * Fiche d'un client existant : sections en onglets, navigation libre.
 *
 * Informations, options et tarification se modifient librement puis
 * s'enregistrent ensemble, après le récapitulatif des seules modifications
 * (avant / après). Logo, configuration VROOM, documents et administrateurs
 * sont enregistrés dès l'action. Tout est tracé dans l'historique du client.
 */
export default function ClientSheet({
  clientId,
  section,
  onSectionChange,
  isJustCreated: isJustCreatedProp,
  creationReport: creationReportProp,
  onExit,
}) {
  // Gardés tant que la fiche est ouverte : changer d'onglet réécrit l'URL et
  // efface `created` et l'état de navigation.
  const [isJustCreated] = useState(isJustCreatedProp);
  const [creationReport] = useState(creationReportProp);
  const [savedClient, setSavedClient] = useState(null);
  const [information, setInformation] = useState(null);
  const [featuresV2, setFeaturesV2] = useState({});
  const [pricing, setPricing] = useState({});
  const [templates, setTemplates] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  const { data: clientsData } = useGetRequest('/super-admin/clients', 0);
  const { data: defaults } = useGetRequest('/super-admin/clients/defaults', 0);
  const { isLoading: isFetching, fetchClient } = useGetClient();
  const { isLoading: isSaving, saveClient } = useSaveClient();
  const { isLoading: isLogoUploading, uploadLogo } = useUploadClientLogo();
  const { isUploading, archivingId, uploadTemplate, archiveTemplate } =
    useClientDocumentTemplates();
  const { isLoading: isCreatingAdmin, createAdminUser } =
    useCreateClientAdminUser();
  const { isLoading: isArchivingAdmin, archiveAdminUser } =
    useArchiveClientAdminUser();
  const { isLoading: isCreatingVroom, createVroomConfiguration } =
    useCreateClientVroomConfiguration();

  const hydrate = (client) => {
    setSavedClient(client);
    setInformation(toInformation(client));
    setFeaturesV2(client.features_v2 || {});
    setPricing(client.pricing || {});
    setTemplates(client.document_templates || []);
    setAdminUsers(client.admin_users || []);
  };

  // fetchClient et onExit sont recréés à chaque rendu : ils ne figurent pas
  // dans les dépendances, sinon la fiche se rechargerait en boucle. Une
  // réponse arrivée après le démontage (retour à la liste) est ignorée.
  useEffect(() => {
    let isActive = true;

    const loadClient = async () => {
      const client = await fetchClient(clientId);

      if (!isActive) return;

      if (!client) {
        onExit();
        return;
      }

      hydrate(client);
    };

    loadClient();

    return () => {
      isActive = false;
    };
  }, [clientId]);

  const clientsOptions = useMemo(
    () => toClientsOptions(clientsData?.clients, clientId),
    [clientsData, clientId]
  );

  const unsavedChanges = useMemo(
    () =>
      savedClient && information
        ? computeJsonDiff(
            toClientPayload({
              information: toInformation(savedClient),
              featuresV2: savedClient.features_v2 || {},
              pricing: savedClient.pricing || {},
            }),
            toClientPayload({ information, featuresV2, pricing })
          )
        : [],
    [savedClient, information, featuresV2, pricing]
  );
  const hasUnsavedChanges = unsavedChanges.length > 0;
  const isFormValid =
    information != null &&
    isInformationStepValid(information) &&
    isPricingValid(pricing);

  const changesBySection = unsavedChanges.reduce((counts, change) => {
    const changeSection = getChangeSection(change.path);
    return { ...counts, [changeSection]: (counts[changeSection] || 0) + 1 };
  }, {});

  const handleCopyFromClient = async (sourceClientId) => {
    const sourceClient = await fetchClient(sourceClientId);
    if (!sourceClient) return;

    setFeaturesV2(sourceClient.features_v2 || {});
    setPricing({
      ...(defaults?.pricing || {}),
      ...(sourceClient.pricing || {}),
    });
  };

  const handleLogoFile = async (file) => {
    const url = await uploadLogo(clientId, file);
    if (url) setSavedClient((previous) => ({ ...previous, logo_url: url }));
  };

  const confirmSave = async () => {
    const client = await saveClient(
      clientId,
      toClientPayload({ information, featuresV2, pricing })
    );
    if (!client) return;

    hydrate(client);
    setIsReviewOpen(false);
  };

  const discardChanges = () => hydrate(savedClient);

  const exit = () => {
    if (hasUnsavedChanges) {
      setIsLeaveConfirmOpen(true);
      return;
    }
    onExit();
  };

  const isLoaded = savedClient != null && information != null;
  const pendingInvitationsCount = adminUsers.filter(
    (adminUser) => adminUser.invitation
  ).length;

  return (
    <div className="ic-page">
      <AgvPageHeader
        icon={faBuilding}
        breadcrumb={['Outils internes', 'Clients', savedClient?.name || '…']}
        title={savedClient?.name || 'Chargement du client…'}
        count={isLoaded ? `ID ${clientId}` : undefined}
        summary={
          isLoaded
            ? [
                savedClient.city,
                savedClient.siret && `SIRET ${savedClient.siret}`,
              ]
                .filter(Boolean)
                .join(' · ')
            : null
        }
        actions={
          <>
            <PageHeaderButton glyph="↺" onClick={() => setIsHistoryOpen(true)}>
              Historique
            </PageHeaderButton>
            <PageHeaderButton glyph="←" onClick={exit}>
              Liste des clients
            </PageHeaderButton>
          </>
        }
        activeTab={section}
        onTabChange={onSectionChange}
        tabs={SECTION_TABS.map((tab) => ({
          ...tab,
          count:
            tab.key === SHEET_SECTION.ADMINS
              ? pendingInvitationsCount
              : changesBySection[tab.key],
          title:
            tab.key === SHEET_SECTION.ADMINS
              ? 'Administrateurs dont le mot de passe reste à définir'
              : 'Modifications non enregistrées',
        }))}
      />

      <div className="ic-page__body">
        <div className="ic-page__content">
          {creationReport?.isLogoMissing && (
            <Banner tone="warn">
              Le logo n’a pas pu être envoyé : déposez-le de nouveau dans
              l’onglet Informations.
            </Banner>
          )}
          {creationReport?.failedAdminEmails?.length > 0 && (
            <Banner tone="warn">
              Administrateur
              {creationReport.failedAdminEmails.length > 1 ? 's' : ''} non créé
              {creationReport.failedAdminEmails.length > 1 ? 's' : ''} :{' '}
              {creationReport.failedAdminEmails.join(', ')}. Corrigez puis
              ajoutez-le
              {creationReport.failedAdminEmails.length > 1 ? 's' : ''} dans
              l’onglet Administrateurs.
            </Banner>
          )}
          {isJustCreated && (
            <Banner>
              Client créé avec sa configuration VROOM par défaut.
              {pendingInvitationsCount > 0 &&
                !isFlagEnabled(savedClient?.is_email_sending_enabled) &&
                ' Envoi d’emails désactivé : transmettez aux administrateurs leur lien d’inscription (onglet Administrateurs).'}
            </Banner>
          )}

          {!isLoaded ? (
            <Panel>
              <p className="tw-m-0 tw-text-[13px] tw-text-[#76808c]">
                Chargement du client…
              </p>
            </Panel>
          ) : (
            <>
              {section === SHEET_SECTION.INFORMATION && (
                <InformationStep
                  isEdition
                  information={information}
                  setInformation={setInformation}
                  logoUrl={savedClient.logo_url}
                  setLogoFile={handleLogoFile}
                  isLogoUploading={isLogoUploading}
                >
                  <VroomSection
                    vroomConfiguration={savedClient.vroom_configuration}
                    isCreating={isCreatingVroom}
                    onCreate={async () => {
                      const created = await createVroomConfiguration(clientId);
                      if (created)
                        setSavedClient((previous) => ({
                          ...previous,
                          vroom_configuration: created,
                        }));
                    }}
                  />
                </InformationStep>
              )}
              {section === SHEET_SECTION.OPTIONS && (
                <OptionsStep
                  featuresV2={featuresV2}
                  setFeaturesV2={setFeaturesV2}
                  defaultFeaturesV2={defaults?.features_v2}
                  clientsOptions={clientsOptions}
                  onCopyFromClient={handleCopyFromClient}
                  isCopying={isFetching}
                />
              )}
              {section === SHEET_SECTION.PRICING && (
                <PricingStep
                  isEdition
                  pricing={pricing}
                  setPricing={setPricing}
                />
              )}
              {section === SHEET_SECTION.DOCUMENTS && (
                <DocumentsStep
                  templates={templates}
                  isUploading={isUploading}
                  archivingId={archivingId}
                  onUpload={async (template) => {
                    const created = await uploadTemplate(clientId, template);
                    if (created)
                      setTemplates((previous) => [created, ...previous]);
                    return created != null;
                  }}
                  onArchive={async (templateId) => {
                    const isArchived = await archiveTemplate(
                      clientId,
                      templateId
                    );
                    if (isArchived) {
                      setTemplates((previous) =>
                        previous.filter(
                          (template) => template.id !== templateId
                        )
                      );
                    }
                  }}
                />
              )}
              {section === SHEET_SECTION.ADMINS && (
                <AdminUserStep
                  adminUsers={adminUsers}
                  isAdding={isCreatingAdmin}
                  isRemoving={isArchivingAdmin}
                  // Réglage enregistré : c'est lui que l'API applique à l'envoi.
                  isEmailSendingEnabled={isFlagEnabled(
                    savedClient.is_email_sending_enabled
                  )}
                  onAdd={async (user) => {
                    const users = await createAdminUser(clientId, user);
                    if (users) setAdminUsers(users);
                    return users != null;
                  }}
                  onRemove={async (adminUser) => {
                    const users = await archiveAdminUser(
                      clientId,
                      adminUser.id
                    );
                    if (users) setAdminUsers(users);
                    return users != null;
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="ic-page__footer">
        {hasUnsavedChanges ? (
          <span className="ic-page__footer-hint ic-page__footer-hint--warn">
            {unsavedChanges.length} modification
            {unsavedChanges.length > 1 ? 's' : ''} non enregistrée
            {unsavedChanges.length > 1 ? 's' : ''} (informations, options,
            tarification)
          </span>
        ) : (
          <span className="ic-page__footer-hint">
            Tout est enregistré. Logo, documents et administrateurs sont
            enregistrés dès l’action.
          </span>
        )}
        <div className="ic-page__footer-spacer" />
        {hasUnsavedChanges && (
          <MainButton
            variant="secondary"
            text="Annuler les modifications"
            onClick={discardChanges}
          />
        )}
        <MainButton
          text="Relire et enregistrer"
          onClick={() => setIsReviewOpen(true)}
          disabled={!hasUnsavedChanges || !isFormValid}
        />
      </div>

      <ClientChangesReviewModal
        isOpen={isReviewOpen}
        setIsOpen={setIsReviewOpen}
        clientName={savedClient?.name}
        diff={unsavedChanges}
        onConfirm={confirmSave}
        isSaving={isSaving}
      />

      <ClientHistoryModal
        isOpen={isHistoryOpen}
        setIsOpen={setIsHistoryOpen}
        clientId={clientId}
      />

      <GenericModal
        isOpen={isLeaveConfirmOpen}
        setIsOpen={setIsLeaveConfirmOpen}
        title="Modifications non enregistrées"
        size={MODAL_SIZE_STYLE[MODAL_SIZE.MD]}
        secondaryActionText="Rester"
        secondaryAction={() => setIsLeaveConfirmOpen(false)}
        mainActionText="Quitter sans enregistrer"
        mainActionVariant="danger"
        mainAction={onExit}
      >
        <p className="tw-m-0 tw-text-sm">
          Les modifications du client qui ne sont pas enregistrées (
          {unsavedChanges.length}) seront perdues.
        </p>
      </GenericModal>
    </div>
  );
}
